import { useIssueTypesAndFields } from '@atlassian/jira-business-entity-project';
import { shouldHandleTheError } from '@atlassian/jira-business-error-handling';
import { safeGetValue, type Field } from '@atlassian/jira-business-fields/utils';
import { useFilteredViewContextInput } from '@atlassian/jira-business-filters';
import {
	type ChildrenQueryData,
	useViewUpdater,
	createViewUpdaterCallbacksWithCacheActions,
	useCacheActions,
	type UpdaterReturn,
	getParentIssueId,
} from '@atlassian/jira-business-list-handlers';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { ff } from '@atlassian/jira-feature-flagging';
import { toIssueId } from '@atlassian/jira-shared-types';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import { fieldIds, TIMELINE_PAGE_SIZE_ARGS } from '../../common/constants';
import { getVariables } from '../../common/utils/get-variables';
import { useIssueParentMap } from '../../common/utils/use-issue-parent-map';
import { useIssuesMap } from '../../common/utils/use-issues-map';
import type {
	JWMTimelineViewItems,
	JWMTimelineViewItems_jira_list_JiraWorkManagementViewItemConnection as JWMTimelineViewItemsEdges,
	JWMTimelineViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node as JWMTimelineViewItemsNode,
	JWMTimelineViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraIssueTypeField_issueType as JWMTimelineViewIIssueTypeField,
} from '../get-timeline-view-issues/__generated_apollo__/JWMTimelineViewItems';
import { TIMELINE_VIEW_ITEMS_QUERY } from '../get-timeline-view-issues/gql';
import type { TimelineViewIssuesReturn } from '../get-timeline-view-issues/types';
import { addChildToParentTimeline } from '../get-timeline-view-issues/utils';

// Loads individual items, dealing with the cache updates
export const useTimelineViewUpdater = ({
	childrenQueryData,
}: {
	childrenQueryData?: ChildrenQueryData<TimelineViewIssuesReturn>;
}): UpdaterReturn => {
	const issueTypesAndFields = useIssueTypesAndFields({ issueOperation: 'VIEW' });
	const hierarchyLevels = ff('jwm.visual-hierarchy-milestone-1')
		? issueTypesAndFields.hierarchyLevels
		: undefined;
	const { quickFiltersJql, hierarchyParent, sorting, jqlContext, isHidingDoneItems } =
		useFilteredViewContextInput({
			view: 'timeline',
		});

	const { addToCache, removeFromCache, updateItemsInCache } = useCacheActions();
	const [{ issueParentMap }] = useIssueParentMap();
	const [{ issuesMap }] = useIssuesMap();
	const cloudId = useCloudId();

	const topLevelVariables = {
		...getVariables({
			jqlContext,
			fieldIds,
			quickFiltersJql,
			isHidingDoneItems,
			hierarchyParent,
			...(ff('jwm.visual-hierarchy-milestone-1') &&
				hierarchyLevels !== undefined && {
					// the above check will be unneccessary once the FF is removed
					hierarchyLevel: hierarchyLevels[0]?.level,
				}),
			sorting,
		}),
		...TIMELINE_PAGE_SIZE_ARGS,
	};

	const { addToCacheCallback, removeFromCacheCallback } =
		createViewUpdaterCallbacksWithCacheActions<JWMTimelineViewItemsNode, JWMTimelineViewItems>({
			query: TIMELINE_VIEW_ITEMS_QUERY,
			addToCache,
			updateItemsInCache,
			removeFromCache,
			isHierarchyEnabled: () => typeof hierarchyParent !== 'undefined',
			isInfiniteHierarchyEnabled: () => !!ff('jwm.visual-hierarchy-milestone-1'),
			createVariablesForItemWithoutParent: () => topLevelVariables,
			createVariablesForItemWithParent: (parentIssueId) => {
				if (!hierarchyParent) {
					// this scenario should be impossible because there is isHierarchyEnabled parameter above
					return topLevelVariables;
				}

				return {
					...getVariables({
						jqlContext,
						fieldIds,
						quickFiltersJql,
						isHidingDoneItems,
						sorting,
						hierarchyParent: undefined,
						parentIssue: {
							hierarchyType: hierarchyParent,
							issueId: parentIssueId,
						},
					}),
					cloudId,
				};
			},
			// We only want to add or remove item if the existing query for children-of-this-parent exists
			// this is to prevent apollo error where the cache key is missing
			shouldChildItemBeAddedToOrRemovedFromCache: (parentIssueId) =>
				Boolean(childrenQueryData && childrenQueryData[toIssueId(parentIssueId)] !== undefined),
			onUpdateParentWithNewChild: addChildToParentTimeline,
			getParentOfIssueIfExist: (issueId) => {
				const maybeIssue = issuesMap.get(issueId);
				if (!maybeIssue) {
					return undefined;
				}
				return getParentIssueId(maybeIssue);
			},
			getPrevParentOfIssueIfExists: (issueId) => issueParentMap.get(String(issueId)),
			isItemHierarchySupported: ff('jwm.visual-hierarchy-milestone-1')
				? undefined
				: (item: JWMTimelineViewItemsNode) => {
						const issueType = safeGetValue<JWMTimelineViewIIssueTypeField | null>(
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							item.fields as unknown as Field[],
							'issuetype',
							'issueType',
							null,
						);
						// this method gets called if the item has a parent
						// if the item is a task, we don't support hierarchy
						// we return false to not update the parent query because that query doesn't exist
						if (issueType?.hierarchy?.level === 0) {
							return false;
						}
						return true;
					},
		});

	return useViewUpdater<JWMTimelineViewItemsNode, JWMTimelineViewItemsEdges, JWMTimelineViewItems>({
		input: {
			fieldIds,
			quickFiltersJql,
			isHidingDoneItems,
		},
		query: TIMELINE_VIEW_ITEMS_QUERY,
		actions: [{ addToCache: addToCacheCallback, removeFromCache: removeFromCacheCallback }],
		onError: (apolloError) => {
			if (!shouldHandleTheError(apolloError)) {
				return;
			}

			fireErrorAnalytics({
				meta: {
					id: 'timelineViewUpdaterQuery',
					packageName: 'jiraBusinessTimeline',
					teamName: 'jira-yurree',
				},
				attributes: {
					message: 'Failed to fetch issues in timeline view updater',
				},
				error: apolloError,
				sendToPrivacyUnsafeSplunk: true,
			});
		},
		onAppendItemsError: (apolloError) => {
			if (!shouldHandleTheError(apolloError)) {
				return;
			}

			fireErrorAnalytics({
				meta: {
					id: 'timelineViewAppendItemsQuery',
					packageName: 'jiraBusinessTimeline',
					teamName: 'jira-yurree',
				},
				attributes: {
					message: 'Failed to append issues by id in timeline view updater',
				},
				error: apolloError,
				sendToPrivacyUnsafeSplunk: true,
			});
		},
	});
};
